import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaFileInvoiceDollar } from 'react-icons/fa';
import { toast } from 'react-toastify';
import OfferService, { Offer } from '../services/OfferService';
import NumericInput from '../../../components/NumericInput';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';

interface CreateInvoiceFromOfferModalProps {
  isOpen: boolean;
  offer: Offer;
  defaultKind?: 'FULL' | 'DEPOSIT';
  defaultDepositPercent?: number;
  onClose: () => void;
  onCreated?: (invoiceId: number, invoiceNumber: string) => void;
}

export default function CreateInvoiceFromOfferModal({
  isOpen,
  offer,
  defaultKind = 'FULL',
  defaultDepositPercent = 50,
  onClose,
  onCreated,
}: CreateInvoiceFromOfferModalProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const [kind, setKind] = useState<'FULL' | 'DEPOSIT'>(defaultKind);
  const [depositPercent, setDepositPercent] = useState(String(defaultDepositPercent));
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [paymentTermsDays, setPaymentTermsDays] = useState(14);
  const [creating, setCreating] = useState(false);
  const [createdInvoice, setCreatedInvoice] = useState<{ id: number; number: string } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setKind(defaultKind);
      setDepositPercent(String(defaultDepositPercent));
      setCreatedInvoice(null);

      const today = new Date();
      setIssueDate(today.toISOString().split('T')[0]);

      const due = new Date(today);
      due.setDate(due.getDate() + paymentTermsDays);
      setDueDate(due.toISOString().split('T')[0]);
    }
  }, [isOpen, defaultKind, defaultDepositPercent, paymentTermsDays]);

  useEffect(() => {
    if (issueDate) {
      const issue = new Date(issueDate);
      issue.setDate(issue.getDate() + paymentTermsDays);
      setDueDate(issue.toISOString().split('T')[0]);
    }
  }, [issueDate, paymentTermsDays]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const percent = kind === 'DEPOSIT' ? parseFloat(depositPercent) || 50 : undefined;
      const result = await OfferService.createInvoice(offer.id, kind, percent);

      setCreatedInvoice({ id: result.invoice_id, number: result.invoice_number });
      toast.success(`Invoice ${result.invoice_number} created (Draft)`);

      if (onCreated) {
        onCreated(result.invoice_id, result.invoice_number);
      }
    } catch (err: any) {
      console.error('Failed to create invoice:', err);
      const message = err?.response?.data?.detail || 'Failed to create invoice';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handleViewInvoice = () => {
    if (createdInvoice) {
      navigate(`/invoices/${createdInvoice.id}`);
      onClose();
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const calculateDepositAmount = (): number => {
    const percent = parseFloat(depositPercent) || 0;
    return (offer.total_gross * percent) / 100;
  };

  if (!isOpen) return null;

  const inputStyle: React.CSSProperties = {
    ...ps.inputStyle,
  };

  const labelStyle: React.CSSProperties = {
    ...ps.labelStyle,
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '12px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: shadows.elevated,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FaFileInvoiceDollar size={20} color={colors.navy} />
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: colors.navy }}>
              Create invoice for offer {offer.offer_number}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: colors.textMuted,
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {createdInvoice ? (
            /* Success State */
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: colors.successLight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <FaFileInvoiceDollar size={28} color={colors.success} />
              </div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: colors.navy }}>
                Invoice created!
              </h3>
              <p style={{ margin: '0 0 24px 0', color: colors.textMuted }}>
                {createdInvoice.number} is ready as draft
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  onClick={handleViewInvoice}
                  style={{
                    ...ps.buttonPrimary,
                  }}
                >
                  View invoice
                </button>
                <button
                  onClick={onClose}
                  style={{
                    ...ps.buttonSecondary,
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            /* Form */
            <>
              {/* Offer Info */}
              <div
                style={{
                  padding: '12px 16px',
                  background: colors.bgMuted,
                  borderRadius: '8px',
                  marginBottom: '20px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: colors.textMuted, fontSize: '14px' }}>Offer total:</span>
                  <span style={{ fontWeight: 700, fontSize: '18px', color: colors.navy }}>
                    {formatCurrency(offer.total_gross)}
                  </span>
                </div>
              </div>

              {/* Kind Selection */}
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Invoice type</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <label
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      border: kind === 'FULL' ? `2px solid ${colors.navy}` : `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: kind === 'FULL' ? colors.infoLight : colors.bgCard,
                    }}
                  >
                    <input
                      type="radio"
                      name="kind"
                      checked={kind === 'FULL'}
                      onChange={() => setKind('FULL')}
                    />
                    <div>
                      <div style={{ fontWeight: 600, color: colors.navy }}>Full invoice</div>
                      <div style={{ fontSize: '12px', color: colors.textMuted }}>Schlussrechnung</div>
                    </div>
                  </label>
                  <label
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      border: kind === 'DEPOSIT' ? `2px solid ${colors.navy}` : `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: kind === 'DEPOSIT' ? colors.infoLight : colors.bgCard,
                    }}
                  >
                    <input
                      type="radio"
                      name="kind"
                      checked={kind === 'DEPOSIT'}
                      onChange={() => setKind('DEPOSIT')}
                    />
                    <div>
                      <div style={{ fontWeight: 600, color: colors.navy }}>Deposit invoice</div>
                      <div style={{ fontSize: '12px', color: colors.textMuted }}>Anzahlungsrechnung</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Deposit Percent */}
              {kind === 'DEPOSIT' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Deposit percentage</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '100px' }}>
                      <NumericInput
                        value={depositPercent}
                        onChange={setDepositPercent}
                        decimals={2}
                        min={1}
                        max={100}
                        placeholder="50.00"
                      />
                    </div>
                    <span style={{ color: colors.textMuted }}>%</span>
                  </div>
                  <div
                    style={{
                      marginTop: '12px',
                      padding: '12px 16px',
                      background: '#fef3c7',
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: '#92400e',
                    }}
                  >
                    This will invoice <strong>{depositPercent}%</strong> of {formatCurrency(offer.total_gross)} ={' '}
                    <strong>{formatCurrency(calculateDepositAmount())}</strong>
                  </div>
                </div>
              )}

              {/* Dates */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Issue date</label>
                  <input
                    type="date"
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Due date</label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Payment Terms */}
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Payment terms (days)</label>
                <select
                  value={paymentTermsDays}
                  onChange={(e) => setPaymentTermsDays(parseInt(e.target.value, 10))}
                  style={{ ...inputStyle, background: colors.bgCard, width: '150px' }}
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={45}>45 days</option>
                  <option value={60}>60 days</option>
                </select>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={creating}
                  style={{
                    ...ps.buttonSecondary,
                    cursor: creating ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  style={{
                    ...(creating ? ps.buttonDisabled : ps.buttonPrimary),
                  }}
                >
                  {creating ? 'Creating...' : 'Create invoice'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
